<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="color"
    :minimized-width="minimizedWidth"
    :minimized="isMinimized"
    :animated="true"
  >
    <!-- Collapsed Sidebar - Show only icons -->
    <template v-if="isMinimized">
      <div class="collapsed-sidebar">
        <VaSidebarItem
          v-for="(route, index) in filteredRoutes"
          :key="index"
          :to="route.children || route.meta?.navDisabled ? undefined : { name: route.name }"
          :active="routeHasActiveChild(route)"
          :active-color="activeColor"
          :text-color="textColor(route)"
          :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${navT(route.displayName)}`"
          role="button"
          hover-opacity="0.10"
          :class="['collapsed-item', { 'nav-item-disabled': route.meta?.navDisabled }]"
        >
          <VaSidebarItemContent class="py-3 px-2 flex justify-center">
            <VaIcon
              v-if="route.meta.icon"
              aria-hidden="true"
              :name="route.meta.icon"
              size="24px"
              :color="iconColor(route)"
            />
          </VaSidebarItemContent>
          <!-- Tooltip for collapsed items -->
          <VaTooltip :message="navT(route.displayName)" placement="right" :disabled="!isMinimized" />
        </VaSidebarItem>
      </div>
    </template>

    <!-- Expanded Sidebar - Show full navigation -->
    <template v-else>
      <VaAccordion v-model="value" multiple>
        <VaCollapse v-for="(route, index) in filteredRoutes" :key="index">
          <template #header="{ value: isCollapsed }">
            <VaSidebarItem
              :to="route.children || route.meta?.navDisabled ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="textColor(route)"
              :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${navT(route.displayName)}`"
              :class="{
                'nav-item-disabled': route.meta?.navDisabled,
                'nav-item-disabled--expandable': route.meta?.navDisabled && route.children?.length,
              }"
              role="button"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-4">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                />
                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                  {{ navT(route.displayName) }}
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>
          <template #body>
            <template v-for="(childRoute, index2) in route.children" :key="index2">
              <!-- Child with its own submenu (e.g. Docs > Main, System) -->
              <div v-if="childRoute.children?.length" class="sidebar-nested-group">
                <div class="sidebar-nested-label py-2 pr-2 pl-11 flex items-center gap-2 text-secondary">
                  <VaIcon v-if="childRoute.meta?.icon" :name="childRoute.meta.icon" size="18px" />
                  <span class="font-semibold text-sm">{{ navT(childRoute.displayName) }}</span>
                </div>
                <div v-for="(grandchild, index3) in childRoute.children" :key="index3" class="sidebar-nested-item">
                  <VaSidebarItem
                    :to="grandchild.meta?.navDisabled ? undefined : { name: grandchild.name }"
                    :active="isActiveChildRoute(grandchild)"
                    :active-color="activeColor"
                    :text-color="textColor(grandchild)"
                    :aria-label="`Visit ${navT(grandchild.displayName)}`"
                    :class="{ 'nav-item-disabled': grandchild.meta?.navDisabled }"
                    hover-opacity="0.10"
                  >
                    <VaSidebarItemContent class="py-2 pr-2 pl-14">
                      <VaSidebarItemTitle class="leading-5 font-semibold text-sm">
                        {{ navT(grandchild.displayName) }}
                      </VaSidebarItemTitle>
                    </VaSidebarItemContent>
                  </VaSidebarItem>
                </div>
              </div>
              <!-- Child without submenu (single link) -->
              <div v-else>
                <VaSidebarItem
                  :to="childRoute.meta?.navDisabled ? undefined : { name: childRoute.name }"
                  :active="isActiveChildRoute(childRoute)"
                  :active-color="activeColor"
                  :text-color="textColor(childRoute)"
                  :aria-label="`Visit ${navT(childRoute.displayName)}`"
                  :class="{ 'nav-item-disabled': childRoute.meta?.navDisabled }"
                  hover-opacity="0.10"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-11">
                    <VaSidebarItemTitle class="leading-5 font-semibold">
                      {{ navT(childRoute.displayName) }}
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </VaSidebarItem>
              </div>
            </template>
          </template>
        </VaCollapse>
      </VaAccordion>
    </template>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed, onUnmounted, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { getNavigationRoutes, type UserRole } from '@/services/navigationService'
import { applyNavigationPolicy } from '@/utils/navigationPolicy'
import { resolveSessionUserRole, syncUserRoleToStorage } from '@/utils/userRoleUtils'
export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t, te } = useI18n()

    const navT = (key: string) => (te(key) ? t(key) : key)

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isFinanceLedgerNavActive = () =>
      route.name === 'finance-ledgers' || (route.name === 'our-driver-single' && route.query.tab === 'finance-ledger')

    const isOurDriversNavActive = () =>
      route.name === 'our-drivers' || (route.name === 'our-driver-single' && route.query.tab !== 'finance-ledger')

    const isOurVehiclesNavActive = () => route.name === 'our-vehicles' || route.name === 'our-vehicle-single'

    const isImmobiliserNavActive = () =>
      route.name === 'immobiliser' ||
      route.name === 'immobiliser-vehicle' ||
      route.name === 'immobiliser-history-detail'

    const isActiveChildRoute = (child: INavigationRoute) => {
      if (child.name === 'finance-ledgers') return isFinanceLedgerNavActive()
      if (child.name === 'our-drivers') return isOurDriversNavActive()
      if (child.name === 'our-vehicles') return isOurVehiclesNavActive()
      if (child.name === 'immobiliser') return isImmobiliserNavActive()
      return route.name === child.name
    }

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name || route.path.endsWith(`${section.name}`)
      }
      const hasActive = (child: INavigationRoute) =>
        isActiveChildRoute(child) ||
        route.path.endsWith(`${child.name}`) ||
        (child.children?.some((c) => hasActive(c)) ?? false)
      return section.children.some(hasActive)
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const isMinimized = computed(() => props.minimized)
    const minimizedWidth = computed(() => '64px')
    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      if (isMinimized.value) return minimizedWidth.value
      return '280px'
    })
    const color = computed(() => getColor('#051436'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : '#ffffff')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const readUserData = (): Record<string, unknown> => {
      try {
        const storedData = localStorage.getItem('userData')
        return storedData ? (JSON.parse(storedData) as Record<string, unknown>) : {}
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error)
        return {}
      }
    }

    const userData = ref<Record<string, unknown>>(readUserData())

    const updateUserData = () => {
      userData.value = readUserData()
      if (Object.keys(userData.value).length > 0) {
        syncUserRoleToStorage(userData.value as Record<string, unknown>)
      }
    }

    // Listen for storage events (when localStorage changes in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userData') {
        updateUserData()
      }
    }

    const handleUserDataUpdate = () => {
      updateUserData()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('userDataUpdated', handleUserDataUpdate)

    // Cleanup event listeners on component unmount
    onUnmounted(() => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('userDataUpdated', handleUserDataUpdate)
    })

    const getUserRole = () => resolveSessionUserRole(userData.value as Record<string, unknown>)

    const userScopes = computed<string[]>(() => {
      const scopes = (userData.value as { scopes?: unknown })?.scopes
      return Array.isArray(scopes) ? (scopes as string[]) : []
    })

    /** Drops entries whose `requiredScope` the user does not hold. Cosmetic: the API enforces it. */
    const filterRoutesByScope = (routes: INavigationRoute[]): INavigationRoute[] =>
      routes
        .filter((route) => !route.meta?.requiredScope || userScopes.value.includes(route.meta.requiredScope))
        .map((route) => (route.children ? { ...route, children: filterRoutesByScope(route.children) } : route))

    const filteredRoutes = computed(() => {
      const userRole = getUserRole()

      // Admin-style roles should always see the full main console sidebar
      const navRole = userRole.toLowerCase()
      if (navRole === 'admin' || navRole === 'manager' || navRole === 'org_admin') {
        return applyNavigationPolicy(filterRoutesByScope(navigationRoutes.routes))
      }

      const routesConfig = getNavigationRoutes(userRole as UserRole)
      return applyNavigationPolicy(filterRoutesByScope(routesConfig.routes))
    })

    // Initialize user data on mount
    onMounted(() => {
      updateUserData()
      console.log('AppSidebar mounted, user role:', getUserRole())
    })

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    // Watch for changes in userData to update routes
    watch(
      userData,
      () => {
        console.log('User data changed, updating routes')
      },
      { deep: true },
    )

    return {
      writableVisible,
      sidebarWidth,
      minimizedWidth,
      isMinimized,
      value,
      color,
      activeColor,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      navT,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>

<style scoped>
/* Override any uppercase styling for sidebar text */
.va-sidebar-item-title {
  text-transform: capitalize !important;
}

/* Ensure all sidebar text is capitalized */
.va-sidebar-item-content {
  text-transform: capitalize !important;
}

/* Override any global uppercase styles for sidebar */
.va-sidebar .va-sidebar-item-title {
  text-transform: capitalize !important;
}

.va-sidebar .va-sidebar-item-content {
  text-transform: capitalize !important;
}

/* Collapsed sidebar styles */
.collapsed-sidebar {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
}

.collapsed-item {
  margin: 0.25rem 0;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.collapsed-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.collapsed-item .va-sidebar-item-content {
  padding: 0.75rem 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Tooltip styles for collapsed items */
.va-tooltip {
  z-index: 1000;
}

/* Smooth transitions for sidebar collapse/expand */
.va-sidebar {
  transition: width 0.3s ease;
}

/* Ensure proper spacing in collapsed mode */
.va-sidebar[data-minimized='true'] {
  min-width: 64px;
  max-width: 64px;
}

.nav-item-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.nav-item-disabled:not(.nav-item-disabled--expandable) {
  pointer-events: none;
}
</style>
